/**
 * NewAccountPopup — Accounts S8b (spec 2 §3b; rulings of 2026-10-27): the New
 * account sheet. It replaces S1's New Account drawer and opens from the shell's
 * New account button and from "+ Create account '{text}'" in the company
 * pickers that create.
 *
 *   - Account name, saved EXACTLY as typed; on all-lower-case input a one-click
 *     "Use '…'?" title-case suggestion (ruling 3).
 *   - A debounced duplicate check (GET /v1/sales/accounts/check — the Accounts
 *     page's own rules, one definition): an account in use → "Open existing
 *     account"; a sparse record (no contacts, no website) → "Rename existing
 *     account" (POST …/renames: renames, adds the website, links contacts —
 *     never a second record); "It's a different company" sets the match aside.
 *   - Website, normalised to a plain domain on blur; a suggestion from a
 *     contact's email domain whose first label is the name's key, free mail
 *     excluded, tagged "✦ From {contact}'s email" (ruling 2).
 *   - "✦ Already in Klaxiora": the contacts whose email domain equals the
 *     website — on by default with no company, off "currently at {company}"
 *     with one (ruling 3); up to 5, "+{n} more" shows the rest.
 *   - Phone stored as typed, shown with the UK display formatter (ruling 4);
 *     Region and Sector (S4); Notes. No owner field (register row 242).
 *   - A live summary; "Add a contact next"; ⌘↵ submits; Esc / × / Cancel ask
 *     before discarding typed work; a toast with Undo (10 s) for a rename and
 *     for links, backed by the rename record's Undo route.
 *
 * Visual: the house sheet (.k-drawer over the scrim, 600 px) and its inputs;
 * the match card wears the warning family (tokens.ts). No new tokens.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { X, Building2, Globe, Phone, MapPin, Briefcase, FileText, Sparkles, AlertTriangle } from 'lucide-react'
import { toast } from 'sonner'
import {
  createAccount, fetchAccountNameCheck, fetchAccountWebsiteSuggestions, fetchContactsByEmailDomain,
  renameAccount, undoAccountRename,
} from '../../api'
import type { SalesAccountNameMatch, SalesAccountRename, SalesAccountWebsiteSuggestion, SalesContactAtDomain } from '../../../../api/types'
import { apiErrorMessage } from '../../../../api/client'
import { overlay, warning } from '../../../../theme/tokens'
import { refreshSalesFigures } from '../../salesFigures'
import { Avatar } from '../../DashboardShared'
import { AccountCodeSelect } from './AccountCodeSelect'
import {
  activeMatch, formatPhoneUK, inUseLine, linkRows, liveSummary, matchKind, sparseLine, titleCaseSuggestion,
  websiteDomain, namesPhrase, renameLabel, renameUndoneLabel,
} from './accountPopup'

/** How the popup finished: the account the caller should show or pick. */
export interface AccountPopupResult {
  kind: 'created' | 'renamed' | 'opened'
  id: string
  name: string
  addContactNext: boolean
}

const VISIBLE_LINKS = 5
const DEBOUNCE_MS = 300

function Group({ title, children }: { title: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-4">
      <div className="text-11 font-semibold uppercase tracking-wide text-ink-muted">{title}</div>
      {children}
    </section>
  )
}

function Field({ label, icon, htmlFor, children, helper }: { label: string; icon: React.ReactNode; htmlFor: string; children: React.ReactNode; helper?: React.ReactNode }) {
  return (
    <div>
      <label htmlFor={htmlFor} className="flex items-center gap-2 text-12 font-semibold text-ink-body mb-2">
        <span className="text-ink-muted">{icon}</span>{label}
      </label>
      {children}
      {helper && <div className="text-11 text-ink-muted mt-1.5">{helper}</div>}
    </div>
  )
}

/** Undo a rename (or a create's links) from its record and say what happened. */
async function undoRename(rec: SalesAccountRename, onChanged: () => void) {
  try {
    const u = await undoAccountRename(rec.id)
    const left = u.notRestored.length
    toast.success(renameUndoneLabel(rec),
      left > 0 ? { description: `${left === 1 ? 'One change had' : `${left} changes had`} been edited since and ${left === 1 ? 'was' : 'were'} left as ${left === 1 ? 'it is' : 'they are'}.` } : undefined)
    onChanged()
  } catch (e) {
    toast.error(apiErrorMessage(e, 'Failed to undo'))
  }
}

export function NewAccountPopup({ initialName = '', onClose, onDone, onChanged, allowAddContact = true }: {
  initialName?: string
  onClose: () => void
  /** The account to show (Accounts page) or pick (a company picker). */
  onDone: (r: AccountPopupResult) => void
  /** Data changed (a create, rename or Undo): the caller re-reads. */
  onChanged?: () => void
  /** false in a company picker: "Add a contact next" is the Accounts page's. */
  allowAddContact?: boolean
}) {
  const [name, setName] = useState(initialName)
  const [website, setWebsite] = useState('')
  const [websiteFrom, setWebsiteFrom] = useState<SalesAccountWebsiteSuggestion | null>(null)
  const [websiteTouched, setWebsiteTouched] = useState(false)
  const [phone, setPhone] = useState('')
  const [region, setRegion] = useState('')
  const [sector, setSector] = useState('')
  const [notes, setNotes] = useState('')
  const [addContactNext, setAddContactNext] = useState(false)
  const [matches, setMatches] = useState<SalesAccountNameMatch[]>([])
  const [checkFailed, setCheckFailed] = useState(false)
  const [dismissed, setDismissed] = useState<Set<string>>(() => new Set())
  const [atDomain, setAtDomain] = useState<SalesContactAtDomain[]>([])
  const [linkOn, setLinkOn] = useState<Record<string, boolean>>({})
  const [showAllLinks, setShowAllLinks] = useState(false)
  const [confirmDiscard, setConfirmDiscard] = useState(false)
  const [saving, setSaving] = useState(false)
  const nameRef = useRef<HTMLInputElement>(null)

  const domain = websiteDomain(website)
  const suggestion = titleCaseSuggestion(name)
  // A cleared name and website ask nothing, so an earlier answer must not linger.
  const match = name.trim() || domain ? activeMatch(matches, dismissed) : null
  const kind = match ? matchKind(match) : null
  const mode: 'create' | 'rename' | 'open' = kind === 'sparse' ? 'rename' : kind ? 'open' : 'create'

  // The duplicate check, debounced, on name and website.
  useEffect(() => {
    const n = name.trim()
    if (!n && !domain) return
    let alive = true
    const t = setTimeout(() => {
      fetchAccountNameCheck(n, domain)
        .then(r => { if (alive) { setMatches(r.items); setCheckFailed(false) } })
        .catch(() => { if (alive) { setMatches([]); setCheckFailed(true) } })
    }, DEBOUNCE_MS)
    return () => { alive = false; clearTimeout(t) }
  }, [name, domain])

  // The website suggestion from contacts' email domains (ruling 2), debounced.
  useEffect(() => {
    const n = name.trim()
    if (!n || websiteTouched) return
    let alive = true
    const t = setTimeout(() => {
      fetchAccountWebsiteSuggestions(n)
        .then(items => {
          if (!alive) return
          const s = items[0] ?? null
          setWebsiteFrom(s)
          setWebsite(s ? s.domain : '')
        })
        .catch(() => { /* a suggestion is optional; the field stays as it is */ })
    }, DEBOUNCE_MS)
    return () => { alive = false; clearTimeout(t) }
  }, [name, websiteTouched])

  // The contacts at the website's domain (ruling 3), debounced.
  useEffect(() => {
    if (!domain) return
    let alive = true
    const t = setTimeout(() => {
      fetchContactsByEmailDomain(domain)
        .then(r => { if (alive) setAtDomain(r.items) })
        .catch(() => { if (alive) setAtDomain([]) })
    }, DEBOUNCE_MS)
    return () => { alive = false; clearTimeout(t) }
  }, [domain])

  const rows = useMemo(() => (domain ? linkRows(atDomain, mode === 'rename' && match ? match.accountId : null) : []), [domain, atDomain, mode, match])
  const isOn = (id: string, def: boolean) => linkOn[id] ?? def
  const linked = mode === 'open' ? [] : rows.filter(r => isOn(r.contact.contactId, r.defaultOn))
  const summary = liveSummary({ kind: mode, name, oldName: match?.name, domain, linkNames: linked.map(r => r.contact.fullName) })
  const dirty = name.trim() !== initialName.trim() || website !== '' || phone !== '' || notes !== '' || region !== '' || sector !== ''

  function requestClose() {
    if (dirty && !confirmDiscard) { setConfirmDiscard(true); return }
    onClose()
  }

  function finish(r: AccountPopupResult) {
    onChanged?.()
    onDone(r)
    onClose()
  }

  async function submit() {
    if (saving) return
    const n = name.trim()
    if (mode === 'open' && match) {
      finish({ kind: 'opened', id: match.accountId, name: match.name, addContactNext })
      return
    }
    if (!n) { toast.error('Account name is required'); nameRef.current?.focus(); return }
    setSaving(true)
    const linkIds = linked.map(r => r.contact.contactId)
    try {
      if (mode === 'rename' && match) {
        const rec = await renameAccount(match.accountId, { name: n, ...(domain ? { website: domain } : {}), linkContactIds: linkIds })
        void refreshSalesFigures()
        toast.success(renameLabel(rec), {
          description: linkIds.length > 0 ? `Linked ${namesPhrase(linked.map(r => r.contact.fullName))}.` : undefined,
          duration: 10_000,
          action: { label: 'Undo', onClick: () => { void undoRename(rec, () => onChanged?.()) } },
        })
        finish({ kind: 'renamed', id: rec.accountId, name: rec.nameAfter, addContactNext })
        return
      }
      const created = await createAccount({
        name: n,
        ...(domain ? { website: domain } : {}),
        ...(phone.trim() ? { phone: phone.trim() } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
        ...(region ? { region } : {}),
        ...(sector ? { sector } : {}),
      })
      void refreshSalesFigures()
      if (linkIds.length > 0) {
        // The links go through the rename record (name unchanged), so their
        // Undo restores each contact's prior link exactly, server-side.
        const rec = await renameAccount(created.id, { name: created.name, linkContactIds: linkIds })
        toast.success(`Account "${created.name}" created`, {
          description: `${renameLabel(rec)}: ${namesPhrase(linked.map(r => r.contact.fullName))}.`,
          duration: 10_000,
          action: { label: 'Undo links', onClick: () => { void undoRename(rec, () => onChanged?.()) } },
        })
      } else {
        toast.success(`Account "${created.name}" created`)
      }
      finish({ kind: 'created', id: created.id, name: created.name, addContactNext })
    } catch (e) {
      toast.error(apiErrorMessage(e, mode === 'rename' ? 'Failed to rename the account' : 'Failed to create account'))
    } finally {
      setSaving(false)
    }
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape') { e.preventDefault(); requestClose(); return }
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); void submit() }
  }

  const inputCls = "w-full px-4 py-3 bg-surface-control border border-edge-control rounded-control text-13 text-ink-body placeholder:text-ink-muted transition-all"
  const restDisabled = mode !== 'create'
  const primary = mode === 'open' ? 'Open existing account' : mode === 'rename' ? 'Rename existing account' : 'Create account'
  const visibleRows = showAllLinks ? rows : rows.slice(0, VISIBLE_LINKS)

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: overlay.scrim }}
      onClick={e => { if (e.target === e.currentTarget) requestClose() }} onKeyDown={onKeyDown}>
      <div className="k-drawer" role="dialog" aria-modal="true" aria-labelledby="new-account-title"
        style={{ width: 'min(600px, 100vw)', animation: 'slide-in-right 220ms cubic-bezier(0.23,1,0.32,1)' }}>

        <div className="flex items-start justify-between px-6 pt-6 pb-4 flex-shrink-0 border-b border-edge-row">
          <div>
            <div id="new-account-title" className="text-detail-title font-bold text-ink-primary">New account</div>
            <div className="text-12 text-ink-muted mt-1">The company your contacts and deals link to. We'll check it isn't already here.</div>
          </div>
          <button type="button" onClick={requestClose} aria-label="Close"
            className="w-8 h-8 flex items-center justify-center rounded-full text-ink-muted hover:bg-surface-nav-hover transition-colors">
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-6">
          <Group title="Company">
            <Field label="Account name *" icon={<Building2 size={12} />} htmlFor="new-account-name"
              helper="Saved exactly as typed. Legal suffixes like Ltd and PLC are kept but ignored when checking for duplicates.">
              <div className="flex items-center gap-3">
                <Avatar name={name.trim() || '?'} size={30} />
                <input id="new-account-name" ref={nameRef} type="text" value={name} autoFocus
                  onChange={e => { setName(e.target.value); setDismissed(new Set()) }}
                  placeholder="Orbital Systems" className={`${inputCls} text-[17px] font-semibold`} />
              </div>
              {suggestion && (
                <button type="button" className="k-btn-quiet mt-2 text-12" onClick={() => setName(suggestion)}>
                  Use '{suggestion}'?
                </button>
              )}
            </Field>

            {match && kind && (
              <div className="rounded-[12px] p-4 flex flex-col gap-3" data-account-match={kind} style={{ background: warning.bg }}>
                <div className="flex items-start gap-2.5">
                  <AlertTriangle size={14} style={{ color: warning.text, flexShrink: 0, marginTop: 2 }} />
                  <div className="text-13 text-ink-body">{kind === 'sparse' ? sparseLine(match) : inUseLine(match)}</div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {kind === 'sparse'
                    ? <button type="button" className="k-btn-secondary" onClick={() => void submit()} disabled={saving || !name.trim()}>Rename "{match.name}" to {name.trim() || '…'}</button>
                    : <button type="button" className="k-btn-secondary" onClick={() => finish({ kind: 'opened', id: match.accountId, name: match.name, addContactNext: false })}>Open {match.name}</button>}
                  <button type="button" className="k-btn-quiet" onClick={() => setDismissed(prev => new Set(prev).add(match.accountId))}>It's a different company</button>
                </div>
              </div>
            )}
            {checkFailed && <div className="text-11 text-ink-muted">Duplicate check unavailable — you can still create the account.</div>}

            <Field label="Website" icon={<Globe size={12} />} htmlFor="new-account-website"
              helper={websiteFrom && !websiteTouched
                ? <span className="inline-flex items-center gap-1"><Sparkles size={11} /> From {websiteFrom.contactName}'s email</span>
                : 'Saved as a plain domain.'}>
              <input id="new-account-website" type="text" value={website}
                onChange={e => { setWebsite(e.target.value); setWebsiteTouched(true); setWebsiteFrom(null) }}
                onBlur={() => setWebsite(w => websiteDomain(w))}
                placeholder="orbital.example" className={inputCls} />
            </Field>

            <Field label="Phone" icon={<Phone size={12} />} htmlFor="new-account-phone"
              helper={restDisabled ? undefined
                : phone.trim() && formatPhoneUK(phone) !== phone.trim() ? `Main switchboard. Shown as ${formatPhoneUK(phone)}.` : 'Main switchboard. Saved as typed.'}>
              <input id="new-account-phone" type="text" value={phone} onChange={e => setPhone(e.target.value)} disabled={restDisabled}
                placeholder="+44 20 …" className={`${inputCls} disabled:opacity-50`} />
            </Field>
          </Group>

          {rows.length > 0 && mode !== 'open' && (
            <Group title={<span className="inline-flex items-center gap-1"><Sparkles size={11} /> Already in Klaxiora</span>}>
              <ul className="flex flex-col gap-2">
                {visibleRows.map(r => {
                  const on = isOn(r.contact.contactId, r.defaultOn)
                  return (
                    <li key={r.contact.contactId} className="flex items-start gap-3">
                      <input type="checkbox" role="switch" id={`link-${r.contact.contactId}`} checked={on} className="mt-1"
                        onChange={e => setLinkOn(prev => ({ ...prev, [r.contact.contactId]: e.target.checked }))} />
                      <label htmlFor={`link-${r.contact.contactId}`} className="text-12 text-ink-body">
                        Link <span className="font-semibold">{r.contact.fullName}</span> to this account · {r.contact.email} matches this website · {r.note}
                      </label>
                    </li>
                  )
                })}
              </ul>
              {rows.length > VISIBLE_LINKS && !showAllLinks && (
                <button type="button" className="k-btn-quiet self-start text-12" onClick={() => setShowAllLinks(true)}>+{rows.length - VISIBLE_LINKS} more</button>
              )}
            </Group>
          )}

          <Group title="More">
            {restDisabled && <div className="text-11 text-ink-muted">Phone, region, sector and notes are not changed by {mode === 'rename' ? 'a rename' : 'opening an account'} — edit the account afterwards.</div>}
            <div className="grid grid-cols-2 gap-4">
              <Field label="Region" icon={<MapPin size={12} />} htmlFor="new-account-region">
                <AccountCodeSelect kind="region" value={region} onChange={setRegion} className={`${inputCls} disabled:opacity-50`} disabled={restDisabled} />
              </Field>
              <Field label="Sector" icon={<Briefcase size={12} />} htmlFor="new-account-sector">
                <AccountCodeSelect kind="sector" value={sector} onChange={setSector} className={`${inputCls} disabled:opacity-50`} disabled={restDisabled} />
              </Field>
            </div>
            <Field label="Notes" icon={<FileText size={12} />} htmlFor="new-account-notes">
              <textarea id="new-account-notes" value={notes} onChange={e => setNotes(e.target.value)} rows={3} disabled={restDisabled}
                placeholder="What they do, how they buy, who matters" className={`${inputCls} resize-none disabled:opacity-50`} />
            </Field>
          </Group>
        </div>

        <div className="flex flex-col gap-3 px-6 pt-4 pb-14 border-t border-edge-row flex-shrink-0">
          <div className="text-12 text-ink-body inline-flex items-start gap-1.5" data-live-summary>
            <Sparkles size={12} className="mt-0.5 flex-shrink-0" /> <span>{summary}</span>
          </div>
          {confirmDiscard ? (
            <div className="flex items-center justify-between gap-3">
              <span className="text-12 text-ink-body">Discard this new account?</span>
              <div className="flex gap-2">
                <button type="button" className="k-btn-secondary" onClick={() => setConfirmDiscard(false)}>Keep editing</button>
                <button type="button" className="k-btn" onClick={onClose}>Discard</button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-3">
              {allowAddContact && mode !== 'open'
                ? <label className="inline-flex items-center gap-2 text-12 text-ink-body">
                    <input type="checkbox" checked={addContactNext} onChange={e => setAddContactNext(e.target.checked)} /> Add a contact next
                  </label>
                : <span />}
              <div className="flex gap-2">
                <button type="button" onClick={requestClose} className="k-btn-secondary">Cancel</button>
                <button type="button" onClick={() => void submit()} disabled={saving} title="⌘↵"
                  className="k-btn k-btn--lg disabled:opacity-50">
                  {saving ? 'Saving…' : primary}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}
